// src/components/avatar/avatarParts.js
//
// THE AVATAR ARTWORK (v2). Every student and educator avatar is drawn from
// these parts, so they all share one look: a big round head, big shiny
// eyes, rosy cheeks, a head-and-chest shot in their clothes.
//
// A "look" is a small object of choices:
//
//   { v: 2, skin, hair, hairColor, eyes, eyeColor, mouth,
//     top, topColor, glasses, pin, bg, blush, freckles }
//
//   skin / eyes / eyeColor / mouth / bg  numbers (free)
//   hair                                 a HAIRSTYLES id (free)
//   hairColor, top, topColor,
//   glasses (glasses or sunglasses), pin catalogue ids (avatarCatalog.json);
//                                        some cost coins
//
// Everything is drawn in a 100 x 100 box, the same box the shop's hats,
// wings, pets and frames are placed in (wearableArt.js SLOT_BOX).
//
// v1 looks (numbers for hair, shirt…) are upgraded by cleanLook().

import React, { useId } from "react";
import { CATALOG_BY_ID, isHiddenItem, itemsOfKind, priceOf } from "./avatarCatalog";
import { useAvatarItemsVersion } from "../../services/avatarItemsService";
import { Eyewear, Pin, Top, paintDefs } from "./avatarWear";
import { HairDeco, SeasonBg, SeasonBgTall, SeasonEyewear, SeasonPin, SeasonTop, outfitLegs } from "./seasons/seasonArt";

export const INK = "#3a2620";

export const SKINS = ["#ffe6d2", "#fbd6b9", "#f3c4a0", "#e8b088", "#dc9d6f", "#c98657", "#b07045", "#935a36", "#784629", "#5c331d"];
export const EYE_COLORS = ["#4a2c1c", "#2b1a12", "#6b4a2b", "#2f6db5", "#3c8a4f", "#6c8a99"];
export const EYE_NAMES = ["Shiny", "Lashes", "Happy", "Sparkle", "Wink"];
export const MOUTH_NAMES = ["Smile", "Big smile", "Laugh", "Little smile", "Wow"];
export const BACKGROUNDS = ["#dcecff", "#fff1c7", "#ffdcea", "#d9f7e3", "#ece0ff", "#ffe2cc", "#d3f1fb", "#ffd9d9", "#e7ebf0", "#d6dcff"];

// [id, name, group]
export const HAIRSTYLES = [
  ["short", "Short & neat", "short"],
  ["sidepart", "Side part", "short"],
  ["messy", "Messy", "short"],
  ["crew", "Crew cut", "short"],
  ["buzz", "Buzz cut", "short"],
  ["lowfade", "Low fade", "short"],
  ["midfade", "Mid fade", "short"],
  ["highfade", "High fade", "short"],
  ["spiky", "Spiky", "short"],
  ["curtain", "Curtain hair", "short"],
  ["middlepart", "Middle part", "short"],
  ["shag", "Shaggy", "short"],
  ["topknot", "Top knot", "short"],
  ["curlytop", "Curly top fade", "textured"],
  ["hightop", "High-top fade", "textured"],
  ["twists", "Short twists", "textured"],
  ["cornrows", "Cornrows", "textured"],
  ["afro", "Rounded afro", "textured"],
  ["curlywaves", "Curly waves", "textured"],
  ["puff", "Puff", "textured"],
  ["twopuffs", "Two puffs", "textured"],
  ["bantu", "Bantu knots", "textured"],
  ["boxbraids", "Box braids", "textured"],
  ["braidbeads", "Braids with beads", "textured"],
  ["longtwists", "Long twists", "textured"],
  ["naturalcurls", "Natural curls", "textured"],
  ["cornrowbun", "Cornrows bun", "textured"],
  ["long", "Long straight", "long"],
  ["waves", "Loose waves", "long"],
  ["ponytail", "High ponytail", "long"],
  ["messybun", "Messy bun", "long"],
  ["halfup", "Half up, half down", "long"],
  ["pigtails", "Braids (pigtails)", "long"],
  ["sidebraid", "Side braid", "long"],
  ["bob", "Bob", "long"],
  ["bigcurls", "Curls", "long"],
  ["bangs", "Bangs & headband", "long"],
  ["spacebuns", "Space buns", "long"],
  ["blowout", "Straight & sleek", "long"],
  ["scarf", "Head scarf", "long"],
];
export const HAIR_IDS = HAIRSTYLES.map((h) => h[0]);
export const HAIR_GROUPS = [
  ["short", "Short"],
  ["textured", "Curls, braids & twists"],
  ["long", "Long & tied up"],
];

export const FREE_TOPS = itemsOfKind("top").filter((i) => !i.price).map((i) => i.id);

export const DEFAULT_LOOK = {
  v: 2,
  skin: 3,
  hair: "short",
  hairColor: "av-hc-darkbrown",
  eyes: 0,
  eyeColor: 0,
  mouth: 0,
  top: "av-top-hoodie",
  topColor: "av-col-blue",
  glasses: "",
  pin: "",
  eyesItem: "", // paid special eyes (hearts, stars…) — replaces `eyes` when set
  mouthItem: "", // paid special mouth (tongue out, braces…)
  mustache: "",
  hairDeco: "", // Seasons: lights / clips in the hair
  bgItem: "", // Seasons: a holiday background scene (replaces `bg`)
  pants: "", // full body only — "" = the plain blue trousers everyone starts with
  shoes: "", // full body only — "" = the white sneakers everyone starts with
  bg: 0,
  blush: true,
  freckles: false,
};

// Skin fine-tune: `skinShade` 0 (lightest) … SKIN_STEPS (darkest) slides
// smoothly through the 10 tones above. Picking a swatch sets it too.
export const SKIN_STEPS = 40;
const mix = (a, b, t) => {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (sh) => Math.round(((pa >> sh) & 255) + (((pb >> sh) & 255) - ((pa >> sh) & 255)) * t);
  return `#${[16, 8, 0].map((sh) => ch(sh).toString(16).padStart(2, "0")).join("")}`;
};
export const skinFromShade = (shade) => {
  const x = (Math.max(0, Math.min(SKIN_STEPS, shade)) / SKIN_STEPS) * (SKINS.length - 1);
  const i = Math.min(SKINS.length - 2, Math.floor(x));
  return mix(SKINS[i], SKINS[i + 1], x - i);
};
export const shadeForSkin = (i) => Math.round((i * SKIN_STEPS) / (SKINS.length - 1));
export const skinColour = (look) => (Number.isFinite(look.skinShade) ? skinFromShade(look.skinShade) : SKINS[look.skin]);

const NUMS = { skin: SKINS.length, eyes: EYE_NAMES.length, eyeColor: EYE_COLORS.length, mouth: MOUTH_NAMES.length, bg: BACKGROUNDS.length };
const IDS = {
  hairColor: "haircolor",
  top: "top",
  topColor: "color",
  glasses: ["glasses", "sunglasses"],
  pin: "pin",
  eyesItem: "eyes",
  mouthItem: "mouth",
  mustache: "mustache",
  hairDeco: "hairdeco",
  bgItem: "bgitem",
  pants: "pants",
  shoes: "shoes",
};
const OPTIONAL = new Set(["glasses", "pin", "eyesItem", "mouthItem", "mustache", "hairDeco", "bgItem", "pants", "shoes"]);

// v1 → v2
const V1_HAIR = ["short", "buzz", "curlywaves", "afro", "long", "ponytail", "twopuffs", "messybun", "boxbraids", "spiky", "sidepart", "scarf"];
const V1_HC = ["av-hc-black", "av-hc-darkbrown", "av-hc-brown", "av-hc-auburn", "av-hc-darkblonde", "av-hc-blonde", "av-hc-red", "av-hc-gray", "av-hc-blue", "av-hc-pink"];
const V1_SHIRT = ["av-col-pink", "av-col-orange", "av-col-yellow", "av-col-teal", "av-col-blue", "av-col-purple", "av-col-green", "av-col-red", "av-col-navy", "av-col-white"];

export const cleanLook = (look) => {
  const out = { ...DEFAULT_LOOK };
  if (!look || typeof look !== "object") return out;
  let src = look;
  if (look.v !== 2) {
    src = {
      ...look,
      skin: Math.min(9, Math.round((Number(look.skin) || 0) * 1.2)),
      hair: V1_HAIR[look.hair] || "short",
      hairColor: V1_HC[look.hairColor] || DEFAULT_LOOK.hairColor,
      top: "av-top-tee",
      topColor: V1_SHIRT[look.shirt] || DEFAULT_LOOK.topColor,
      glasses: "",
      pin: "",
    };
  }
  Object.entries(NUMS).forEach(([k, n]) => {
    const v = Math.floor(Number(src[k]));
    if (Number.isFinite(v) && v >= 0 && v < n) out[k] = v;
  });
  if (HAIR_IDS.includes(src.hair)) out.hair = src.hair;
  Object.entries(IDS).forEach(([k, kind]) => {
    const it = CATALOG_BY_ID[src[k]];
    const kinds = Array.isArray(kind) ? kind : [kind];
    if (it && kinds.includes(it.kind)) out[k] = src[k];
    else if (OPTIONAL.has(k) && !src[k]) out[k] = "";
  });
  const sh = Math.floor(Number(src.skinShade));
  if (src.skinShade !== undefined && src.skinShade !== null && Number.isFinite(sh) && sh >= 0 && sh <= SKIN_STEPS) out.skinShade = sh;
  out.blush = src.blush === undefined ? true : Boolean(src.blush);
  out.freckles = Boolean(src.freckles);
  return out;
};

/** A random look from the FREE choices only (so 🎲 never costs coins). */
export const randomLook = (owned = new Set(), free = false) => {
  const pick = (a) => a[Math.floor(Math.random() * a.length)];
  const ok = (i) => owned.has(i.id) || (!isHiddenItem(i.id) && ((free && !i.exclusive) || priceOf(i.id) === 0));
  return cleanLook({
    v: 2,
    skin: Math.floor(Math.random() * SKINS.length),
    hair: pick(HAIR_IDS),
    hairColor: pick(itemsOfKind("haircolor").filter(ok)).id,
    eyes: Math.floor(Math.random() * 4),
    eyeColor: Math.floor(Math.random() * EYE_COLORS.length),
    mouth: Math.floor(Math.random() * MOUTH_NAMES.length),
    top: pick(itemsOfKind("top").filter(ok)).id,
    topColor: pick(itemsOfKind("color").filter(ok)).id,
    bg: Math.floor(Math.random() * BACKGROUNDS.length),
    blush: Math.random() < 0.7,
    freckles: Math.random() < 0.2,
  });
};

// ---------------------------------------------------------------------------
// colour helpers
// ---------------------------------------------------------------------------
export const shade = (hex, f) => {
  const n = parseInt(String(hex).slice(1), 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const r = n >> 16;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const m = f > 1 ? (v) => v + (255 - v) * (f - 1) : (v) => v * f;
  return `#${[r, g, b].map((v) => ch(m(v)).toString(16).padStart(2, "0")).join("")}`;
};

// tiny deterministic noise so curls/strands sit the same way every time
const rnd = (i) => {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

// ---------------------------------------------------------------------------
// HAIR building blocks — everything in the 100x100 box. The head is an
// ellipse at (50,44) rx 22.5 ry 22: top y 22, ears at x 27.5 / 72.5.
// ---------------------------------------------------------------------------
const cap = (top = 18, side = 46, hl = 31, w = 0) =>
  `M${26.2 - w} ${side} C${24.5 - w} ${top + 9} ${35} ${top} 50 ${top} C65 ${top} ${75.5 + w} ${top + 9} ${73.8 + w} ${side} C${71} ${hl + 9} ${64} ${hl} 50 ${hl} C36 ${hl} ${29} ${hl + 9} ${26.2 - w} ${side} Z`;

/** circles around an ellipse arc (degrees, 0 = right, 90 = down) */
const bumps = (cx, cy, rx, ry, r, n, a0 = 180, a1 = 360, seed = 1) =>
  Array.from({ length: n }, (_, i) => {
    const a = ((a0 + ((a1 - a0) * i) / Math.max(1, n - 1)) * Math.PI) / 180;
    const j = 0.85 + rnd(i + seed) * 0.3;
    return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry, r * j];
  });

const Curls = ({ list, fill, ink }) => (
  <g>
    {list.map(([x, y, r], i) => (
      <circle key={`o${i}`} cx={x} cy={y} r={r} fill={fill} stroke={ink} strokeWidth="1.1" />
    ))}
    {list.map(([x, y, r], i) => (
      <circle key={`i${i}`} cx={x} cy={y} r={r - 0.6} fill={fill} />
    ))}
    {list.map(([x, y, r], i) => (
      <path key={`h${i}`} d={`M${x - r * 0.5} ${y - r * 0.1} q${r * 0.4} ${-r * 0.55} ${r * 0.9} ${-r * 0.1}`} stroke={shade(fill, 1.35)} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.8" />
    ))}
  </g>
);

/** coil texture: little "c" marks scattered in a box */
const Coils = ({ x0, x1, y0, y1, n = 26, c, seed = 3 }) => (
  <g stroke={c} strokeWidth="0.75" fill="none" strokeLinecap="round" opacity="0.75">
    {Array.from({ length: n }, (_, i) => {
      const x = x0 + rnd(i * 2 + seed) * (x1 - x0);
      const y = y0 + rnd(i * 3 + seed + 1) * (y1 - y0);
      return <path key={i} d={`M${x} ${y} a1.3 1.3 0 1 1 1.6 1.2`} />;
    })}
  </g>
);

/** fine strand lines inside straight hair */
const Strands = ({ list, c }) => (
  <g stroke={c} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.7">
    {list.map((d, i) => (
      <path key={i} d={d} />
    ))}
  </g>
);

/** a plait hanging from (x,y) down `len`, made of overlapping ovals */
const Braid = ({ x, y, len, w = 3.6, fill, ink, bend = 0, beads = null }) => {
  const n = Math.max(3, Math.round(len / (w * 1.1)));
  const step = len / n;
  return (
    <g>
      {Array.from({ length: n }, (_, i) => {
        const yy = y + step * (i + 0.5);
        const xx = x + bend * (i / n) + (i % 2 ? 0.6 : -0.6);
        return <ellipse key={i} cx={xx} cy={yy} rx={w} ry={step * 0.68} fill={fill} stroke={ink} strokeWidth="0.9" transform={`rotate(${i % 2 ? 18 : -18} ${xx} ${yy})`} />;
      })}
      {beads &&
        [0, 1].map((k) => (
          <circle key={k} cx={x + bend} cy={y + len + 1.6 + k * 3.2} r="1.8" fill={beads} stroke={shade(beads, 0.6)} strokeWidth="0.7" />
        ))}
    </g>
  );
};

/** fade on the sides of the head: hair colour fading out toward the ears */
const Fade = ({ gid, c, from, to }) => (
  <g clipPath={`url(#${gid}clip)`}>
    <defs>
      <clipPath id={`${gid}clip`}>
        <ellipse cx="50" cy="44" rx="22.4" ry="21.9" />
      </clipPath>
      <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={c} stopOpacity="0.85" />
        <stop offset="1" stopColor={c} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d={`M26 ${from - 2} L35 ${from - 4} C33 ${from + 4} 33 ${to - 6} 34 ${to} L26 ${to} Z`} fill={`url(#${gid})`} />
    <path d={`M74 ${from - 2} L65 ${from - 4} C67 ${from + 4} 67 ${to - 6} 66 ${to} L74 ${to} Z`} fill={`url(#${gid})`} />
  </g>
);

// Long hair falling behind the shoulders
const longBack = (len = 92, w = 0, wavy = false) => {
  if (!wavy) return `M25 38 C${19 - w} 52 ${18 - w} 74 ${20 - w} ${len} L${80 + w} ${len} C${82 + w} 74 ${81 + w} 52 75 38 Z`;
  let s = `M25 38 C${18 - w} 52 ${16 - w} 70 ${18 - w} ${len - 6}`;
  for (let i = 0; i <= 8; i += 1) s += ` Q${18 - w + i * 8 + 4} ${len + (i % 2 ? 4 : -2)} ${18 - w + (i + 1) * 8} ${len - 3}`;
  return `${s} C${84 + w} 70 ${82 + w} 52 75 38 Z`;
};

/** Returns { back, front } drawings for a hairstyle. */
const hairArt = (style, c, gid, scarfColor) => {
  const ink = shade(c, 0.55);
  const hi = shade(c, 1.35);
  const dk = shade(c, 0.75);
  const P = (d, extra = {}) => <path d={d} fill={c} stroke={ink} strokeWidth="1.2" strokeLinejoin="round" {...extra} />;
  const shortFront = (top, hl, fringe) => (
    <g>
      {P(cap(top, 45, hl))}
      {fringe}
    </g>
  );
  const fringeSoft = (y = 31) => P(`M30 ${y + 6} C34 ${y - 2} 44 ${y - 1} 50 ${y + 3} C55 ${y - 1} 64 ${y - 2} 70 ${y + 6} C64 ${y + 2} 58 ${y + 2} 54 ${y + 5} C50 ${y + 2} 44 ${y + 2} 40 ${y + 5} C36 ${y + 2} 33 ${y + 3} 30 ${y + 6} Z`);

  switch (style) {
    case "short":
      return {
        front: (
          <g>
            {shortFront(17.5, 30, fringeSoft(29))}
            <Strands c={hi} list={["M38 22 q4 3 6 8", "M50 20 q2 4 2 9", "M60 22 q-3 3 -4 8"]} />
          </g>
        ),
      };
    case "sidepart":
      return {
        front: (
          <g>
            {P(cap(16.5, 45, 30))}
            {P("M40 21 C52 16 70 20 74 36 C70 30 62 28 54 30 C48 31 42 33 37 38 C36 30 37 24 40 21 Z")}
            <path d="M40 21 C39 26 37 31 36 37" stroke={ink} strokeWidth="1.1" fill="none" />
            <Strands c={hi} list={["M45 21 q12 -2 22 8", "M44 25 q12 -2 20 6", "M30 30 q2 -6 8 -9"]} />
          </g>
        ),
      };
    case "messy":
      return {
        front: (
          <g>
            {P(`M25.5 45 L24 36 L21 34 L26 30 L25 24 L31 25 L33 18 L39 21 L42 14 L47 19 L52 13 L56 19 L62 15 L64 21 L70 20 L70 26 L77 28 L74 33 L77 38 L74.5 45 C71 39 66 33 60 34 L57 38 L53 33 L48 37 L45 32 L40 37 L37 32 L32 37 Z`)}
            <Strands c={hi} list={["M36 25 l4 5", "M50 20 l1 6", "M62 22 l-3 5", "M44 22 l2 6"]} />
          </g>
        ),
      };
    case "crew":
      return {
        front: (
          <g>
            <Fade gid={gid} c={c} from={33} to={50} />
            {P(cap(19.5, 40, 29.5))}
            <Coils x0={33} x1={66} y0={21} y1={29} n={14} c={dk} seed={7} />
          </g>
        ),
      };
    case "buzz":
      return {
        front: (
          <g>
            <path d={cap(21.3, 44, 29)} fill={c} opacity="0.62" />
            <path d={`M28 44 C26.5 33 37 22.5 50 22.5 C63 22.5 73.5 33 72 44`} fill="none" stroke={ink} strokeWidth="0.7" opacity="0.5" />
            <g fill={dk} opacity="0.7">
              {Array.from({ length: 70 }, (_, i) => {
                const x = 30 + rnd(i * 5 + 1) * 40;
                const y = 23.5 + rnd(i * 7 + 2) * 10 + Math.abs(x - 50) * 0.18;
                return <circle key={i} cx={x} cy={y} r="0.45" />;
              })}
            </g>
          </g>
        ),
      };
    case "lowfade":
    case "midfade":
    case "highfade": {
      const to = style === "lowfade" ? 52 : style === "midfade" ? 46 : 41;
      const sideY = style === "lowfade" ? 42 : style === "midfade" ? 38 : 35;
      return {
        front: (
          <g>
            <Fade gid={gid} c={c} from={sideY - 4} to={to} />
            {P(`M${style === "highfade" ? 31 : 28} ${sideY} C27 26 36 16 50 16 C64 16 73 26 ${style === "highfade" ? 69 : 72} ${sideY} C68 30 60 28 50 29 C40 28 33 30 ${style === "highfade" ? 31 : 28} ${sideY} Z`)}
            {P(`M33 28 L36 21 L40 25 L44 17 L48 23 L53 16 L56 23 L61 18 L63 25 L67 22 L67 30 C60 26 50 27 42 28 Z`)}
            <Strands c={hi} list={["M40 21 l2 4", "M51 19 l1 5", "M60 21 l-2 4"]} />
          </g>
        ),
      };
    }
    case "spiky":
      return {
        front: (
          <g>
            {P(cap(20, 44, 30))}
            {P(`M28 36 L22 26 L31 27 L29 16 L38 22 L41 10 L47 19 L52 8 L56 19 L63 11 L64 22 L72 17 L70 27 L78 27 L72 36 C66 30 58 28 50 29 C42 28 34 30 28 36 Z`)}
            <Strands c={hi} list={["M41 13 l2 8", "M52 11 l0 9", "M62 14 l-2 7"]} />
          </g>
        ),
      };
    case "curtain":
    case "middlepart": {
      const end = style === "curtain" ? 46 : 54;
      return {
        back: style === "middlepart" ? <path d={`M25 40 C22 48 22 56 25 ${end + 4} L75 ${end + 4} C78 56 78 48 75 40 Z`} fill={dk} stroke={ink} strokeWidth="1.1" /> : null,
        front: (
          <g>
            {P(cap(17, 45, 26))}
            {P(`M50 22 C42 23 33 29 30 38 C28 44 27 ${end} 25 ${end + 2} C22 ${end - 6} 22 34 30 25 C37 19 46 18 50 22 Z`)}
            {P(`M50 22 C58 23 67 29 70 38 C72 44 73 ${end} 75 ${end + 2} C78 ${end - 6} 78 34 70 25 C63 19 54 18 50 22 Z`)}
            {P(`M50 22 C44 25 38 31 35 40 C40 33 45 29 50 27 C55 29 60 33 65 40 C62 31 56 25 50 22 Z`)}
            <Strands c={hi} list={["M47 23 q-9 4 -13 14", "M53 23 q9 4 13 14"]} />
          </g>
        ),
      };
    }
    case "shag":
      return {
        back: <path d="M24 38 C20 46 21 58 24 64 L30 60 L33 66 L38 60 L62 60 L67 66 L70 60 L76 64 C79 58 80 46 76 38 Z" fill={dk} stroke={ink} strokeWidth="1.1" />,
        front: (
          <g>
            {P(cap(16, 50, 30, 1.5))}
            {P(`M27 40 L29 34 C34 24 44 22 50 24 C58 22 68 26 72 34 L73 40 L68 36 L66 41 L61 35 L58 40 L53 34 L49 39 L45 34 L41 40 L38 35 L34 41 L31 36 Z`)}
            <path d="M25 44 L24 56 L28 52 L28 60 L31 52" fill={c} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
            <path d="M75 44 L76 56 L72 52 L72 60 L69 52" fill={c} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
            <Strands c={hi} list={["M40 26 q-3 6 -5 12", "M52 26 l0 9", "M62 27 q3 5 4 10"]} />
          </g>
        ),
      };
    case "topknot":
      return {
        front: (
          <g>
            {P(cap(19, 45, 29))}
            <circle cx="50" cy="15" r="7" fill={c} stroke={ink} strokeWidth="1.2" />
            <path d="M44 15 q6 -5 12 0 M45 18 q5 -3 10 0" stroke={hi} strokeWidth="0.8" fill="none" />
            <rect x="45" y="20" width="10" height="2.6" rx="1.3" fill={dk} stroke={ink} strokeWidth="0.8" />
            <Strands c={hi} list={["M36 30 q6 -7 14 -8", "M64 30 q-6 -7 -14 -8"]} />
          </g>
        ),
      };
    case "curlytop":
      return {
        front: (
          <g>
            <Fade gid={gid} c={c} from={34} to={47} />
            {P(cap(20, 39, 30))}
            <Curls fill={c} ink={ink} list={[...bumps(50, 26, 19, 9, 4.3, 9, 185, 355, 2), ...bumps(50, 26, 11, 4, 4.3, 4, 190, 350, 9), [50, 18, 4.4]]} />
          </g>
        ),
      };
    case "hightop":
      return {
        front: (
          <g>
            <Fade gid={gid} c={c} from={30} to={46} />
            {P("M30 36 L31 10 Q33 6 36 7 Q39 4.5 42 6.5 Q45 4.5 48 6.5 Q51 4.5 54 6.5 Q57 4.5 60 6.5 Q63 4.5 66 7 Q69 7 69 10 L70 36 C64 31 57 30 50 30 C43 30 36 31 30 36 Z")}
            <Coils x0={33} x1={66} y0={9} y1={28} n={42} c={dk} seed={5} />
            <Coils x0={34} x1={65} y0={9} y1={26} n={16} c={hi} seed={19} />
          </g>
        ),
      };
    case "twists":
      return {
        front: (
          <g>
            <Fade gid={gid} c={c} from={35} to={47} />
            {P(cap(21, 39, 30))}
            {Array.from({ length: 13 }, (_, i) => {
              const x = 29 + i * 3.5;
              const tilt = (x - 50) * 0.3;
              const top = 15.5 + Math.abs(x - 50) * 0.22;
              return (
                <g key={i}>
                  <path d={`M${x} 30 Q${x + tilt * 0.3} ${top + 8} ${x + tilt} ${top}`} stroke={ink} strokeWidth="4.6" strokeLinecap="round" fill="none" />
                  <path d={`M${x} 30 Q${x + tilt * 0.3} ${top + 8} ${x + tilt} ${top}`} stroke={c} strokeWidth="3.2" strokeLinecap="round" fill="none" />
                  <path d={`M${x - 1} 27 l2 -1.5 M${x - 1 + tilt * 0.3} 22 l2 -1.5 M${x - 1 + tilt * 0.6} 17.5 l2 -1.5`} stroke={hi} strokeWidth="0.7" />
                </g>
              );
            })}
          </g>
        ),
      };
    case "cornrows":
    case "cornrowbun": {
      const rows = style === "cornrows" ? [30, 36, 42, 48, 54, 60, 66, 70] : [32, 40, 48, 56, 64, 68];
      return {
        front: (
          <g>
            <path d={cap(19, 46, 29)} fill={shade(c, 0.85)} stroke={ink} strokeWidth="1.1" />
            {rows.map((x, i) => {
              const endX = style === "cornrows" ? 50 + (x - 50) * 0.4 : 50 + (x - 50) * 0.15;
              const endY = style === "cornrows" ? 18 : 16;
              return (
                <g key={i}>
                  {Array.from({ length: 5 }, (_, k) => {
                    const t = k / 4;
                    const xx = x + (endX - x) * t;
                    const yy = 31 + Math.abs(x - 50) * 0.25 + (endY - 31 - Math.abs(x - 50) * 0.25) * t;
                    return <ellipse key={k} cx={xx} cy={yy} rx="2.1" ry="1.6" fill={c} stroke={ink} strokeWidth="0.6" transform={`rotate(${(x - 50) * 0.8} ${xx} ${yy})`} />;
                  })}
                </g>
              );
            })}
            {style === "cornrowbun" && (
              <g>
                <circle cx="50" cy="12" r="8" fill={c} stroke={ink} strokeWidth="1.2" />
                <path d="M43 10 q7 -6 14 0 M42.5 13.5 q7.5 -5 15 0 M44 17 q6 -4 12 0" stroke={hi} strokeWidth="0.8" fill="none" />
              </g>
            )}
          </g>
        ),
      };
    }
    case "afro":
      return {
        back: <Curls fill={c} ink={ink} list={[...bumps(50, 37, 29, 24, 6.2, 18, 150, 390, 4), [50, 34, 10]]} />,
        front: (
          <g>
            <path d="M24 44 C22 22 38 12 50 12 C62 12 78 22 76 44 C72 34 62 30 50 31 C38 30 28 34 24 44 Z" fill={c} />
            <Curls fill={c} ink={ink} list={bumps(50, 31, 21, 4, 3.6, 8, 195, 345, 8)} />
            <Coils x0={30} x1={70} y0={14} y1={27} n={22} c={hi} seed={9} />
          </g>
        ),
      };
    case "curlywaves":
      return {
        front: (
          <g>
            <Curls fill={c} ink={ink} list={[...bumps(50, 34, 24.5, 18, 5.2, 13, 170, 370, 6), ...bumps(50, 30, 15, 6, 4.6, 6, 200, 340, 13)]} />
            <path d="M28 40 C28 26 38 18 50 18 C62 18 72 26 72 40 C66 34 58 32 50 33 C42 32 34 34 28 40 Z" fill={c} />
            <Curls fill={c} ink={ink} list={bumps(50, 32, 18, 3, 3.8, 7, 190, 350, 21)} />
          </g>
        ),
      };
    case "puff":
    case "twopuffs":
    case "bantu":
    case "spacebuns": {
      const sleek = (
        <g>
          {P(cap(19.5, 46, 29))}
          <path d={style === "puff" ? "M36 31 Q42 24 50 22 Q58 24 64 31" : "M50 20 L50 30"} stroke={ink} strokeWidth="0.9" fill="none" />
          {style !== "spacebuns" && <path d="M29 37 q-2 4 1 6 M71 37 q2 4 -1 6" stroke={ink} strokeWidth="0.8" fill="none" />}
          <Strands c={hi} list={["M34 30 q6 -6 14 -8", "M66 30 q-6 -6 -14 -8"]} />
        </g>
      );
      let extra = null;
      if (style === "puff") extra = <Curls fill={c} ink={ink} list={[...bumps(50, 13, 12, 8.5, 4.6, 14, 0, 335, 3), [50, 13, 9], [44, 12, 5], [56, 12, 5]]} />;
      if (style === "twopuffs")
        extra = (
          <g>
            <Curls fill={c} ink={ink} list={[...bumps(27, 20, 8, 7, 3.8, 10, 0, 330, 5), [27, 20, 6]]} />
            <Curls fill={c} ink={ink} list={[...bumps(73, 20, 8, 7, 3.8, 10, 0, 330, 15), [73, 20, 6]]} />
            <circle cx="33" cy="25" r="2" fill="#ff5fa2" stroke="#b8326d" strokeWidth="0.6" />
            <circle cx="67" cy="25" r="2" fill="#ff5fa2" stroke="#b8326d" strokeWidth="0.6" />
          </g>
        );
      if (style === "spacebuns")
        extra = (
          <g>
            {[30, 70].map((x) => (
              <g key={x}>
                <circle cx={x} cy="17" r="7.5" fill={c} stroke={ink} strokeWidth="1.2" />
                <path d={`M${x - 5} 16 q5 -5 10 0 M${x - 5.5} 19 q5.5 -4 11 0`} stroke={hi} strokeWidth="0.8" fill="none" />
              </g>
            ))}
          </g>
        );
      if (style === "bantu")
        extra = (
          <g>
            {[
              [33, 21],
              [50, 13],
              [67, 21],
              [41, 25],
              [59, 25],
            ].map(([x, y], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r="5" fill={c} stroke={ink} strokeWidth="1.1" />
                <path d={`M${x - 3} ${y} a3 3 0 1 1 3 3`} stroke={hi} strokeWidth="0.8" fill="none" />
              </g>
            ))}
          </g>
        );
      const back = null;
      return {
        back,
        front: (
          <g>
            {style === "twopuffs" ? extra : null}
            {sleek}
            {style !== "twopuffs" ? extra : null}
          </g>
        ),
      };
    }
    case "boxbraids":
    case "longtwists": {
      const xs = [24, 27.5, 31, 69, 72.5, 76];
      const beads = style === "boxbraids" ? "#e9b949" : null;
      return {
        back: (
          <g>
            {[20, 23.5, 27, 30.5, 69.5, 73, 76.5, 80].map((x, i) => (
              <Braid key={i} x={x} y={36} len={52} w={2.4} fill={dk} ink={ink} bend={(x - 50) * 0.08} />
            ))}
          </g>
        ),
        front: (
          <g>
            {P(cap(18.5, 46, 28))}
            <path d="M50 19 L50 28 M40 21 L41 29 M60 21 L59 29" stroke={ink} strokeWidth="0.8" />
            {xs.map((x, i) => (
              <Braid key={i} x={x} y={40} len={42 + (i % 3) * 3} w={2.2} fill={c} ink={ink} bend={(x - 50) * 0.06} beads={i % 2 ? beads : null} />
            ))}
            {style === "longtwists" &&
              [27.5, 72.5].map((x) => <rect key={x} x={x - 2.5} y="60" width="5" height="2.6" rx="1" fill="#e9b949" stroke="#9c7618" strokeWidth="0.6" />)}
          </g>
        ),
      };
    }
    case "braidbeads":
    case "pigtails":
      return {
        front: (
          <g>
            {P(cap(19, 46, 29))}
            <path d="M50 20 L50 29" stroke={ink} strokeWidth="0.9" />
            <Braid x={26} y={44} len={36} w={3.3} fill={c} ink={ink} bend={-2} beads={style === "braidbeads" ? "#cfd6e2" : null} />
            <Braid x={74} y={44} len={36} w={3.3} fill={c} ink={ink} bend={2} beads={style === "braidbeads" ? "#cfd6e2" : null} />
            {style === "pigtails" &&
              [24, 76].map((x) => <rect key={x} x={x - 2.4} y="79" width="4.8" height="2.4" rx="1.2" fill="#ff7ab0" stroke="#b8326d" strokeWidth="0.5" />)}
            <Strands c={hi} list={["M36 30 q6 -6 13 -8", "M64 30 q-6 -6 -13 -8"]} />
          </g>
        ),
      };
    case "naturalcurls":
    case "bigcurls":
      return {
        back: <Curls fill={dk} ink={ink} list={[...bumps(50, 52, 31, 34, 5.6, 22, 150, 390, 2), ...bumps(50, 62, 26, 22, 5, 10, 0, 180, 17)]} />,
        front: (
          <g>
            {P(cap(17, 46, 29, 1))}
            <Curls fill={c} ink={ink} list={[...bumps(50, 31, 20, 11, 4.4, 10, 180, 360, 30), ...bumps(26, 52, 3, 14, 4, 4, 270, 450, 40), ...bumps(74, 52, 3, 14, 4, 4, 270, 450, 50)]} />
          </g>
        ),
      };
    case "long":
    case "waves":
    case "bangs":
    case "blowout":
    case "halfup":
      return {
        back: <path d={longBack(style === "long" || style === "blowout" ? 96 : 90, 1, style === "waves" || style === "halfup")} fill={dk} stroke={ink} strokeWidth="1.2" />,
        front: (
          <g>
            {style === "bangs" ? (
              <g>
                {P(cap(17, 50, 34, 1))}
                {P("M29 40 L30 31 C34 24 44 22 50 22 C56 22 66 24 70 31 L71 40 C66 36 60 36 50 37 C40 36 34 36 29 40 Z")}
                <path d="M34 38 l1 -6 M40 37 l0 -7 M46 37 l0 -8 M52 37 l0 -8 M58 37 l0 -7 M64 38 l-1 -6" stroke={ink} strokeWidth="0.8" />
                <path d="M25 33 C30 18 70 18 75 33" stroke="#ff5fa2" strokeWidth="3.2" fill="none" strokeLinecap="round" />
                <path d="M25 33 C30 18 70 18 75 33" stroke="#ffb3d1" strokeWidth="1" fill="none" strokeLinecap="round" />
              </g>
            ) : style === "blowout" ? (
              <g>
                {P(cap(17, 50, 30, 1))}
                {P("M44 21 C60 18 74 26 76 44 C76 58 77 70 79 78 C73 66 72 54 70 44 C66 34 56 30 44 33 C40 34 34 37 30 42 C31 32 36 24 44 21 Z")}
                <path d="M44 21 C40 26 36 32 30 42" stroke={ink} strokeWidth="1" fill="none" />
              </g>
            ) : (
              <g>
                {P(cap(17, 50, 29, 1))}
                <path d="M50 19 L50 29" stroke={ink} strokeWidth="1" />
                {P(`M50 22 C42 24 32 30 29 42 C27 52 27 64 25 ${style === "waves" || style === "halfup" ? 74 : 80} C22 70 22 50 24 38 C28 26 40 19 50 22 Z`)}
                {P(`M50 22 C58 24 68 30 71 42 C73 52 73 64 75 ${style === "waves" || style === "halfup" ? 74 : 80} C78 70 78 50 76 38 C72 26 60 19 50 22 Z`)}
              </g>
            )}
            {style === "halfup" && (
              <g>
                <circle cx="50" cy="13" r="6" fill={c} stroke={ink} strokeWidth="1.2" />
                <path d="M45 13 q5 -4 10 0" stroke={hi} strokeWidth="0.8" fill="none" />
              </g>
            )}
            {style === "waves" || style === "halfup" ? (
              <Strands c={hi} list={["M27 48 q-2 6 1 10 q3 4 0 8", "M73 48 q2 6 -1 10 q-3 4 0 8", "M40 25 q-6 5 -9 13"]} />
            ) : (
              <Strands c={hi} list={["M40 25 q-7 6 -10 18", "M60 25 q7 6 10 18", "M27 50 l-1 14", "M73 50 l1 14"]} />
            )}
          </g>
        ),
      };
    case "ponytail":
      return {
        back: (
          <g>
            <path d="M66 22 C82 20 88 36 84 52 C82 62 84 70 80 76 C76 66 78 56 76 46 C75 36 72 30 66 28 Z" fill={c} stroke={ink} strokeWidth="1.2" />
            <path d="M78 34 q3 10 1 22" stroke={hi} strokeWidth="0.8" fill="none" />
          </g>
        ),
        front: (
          <g>
            {P(cap(18.5, 46, 29))}
            <ellipse cx="67" cy="23" rx="3.4" ry="2.4" fill="#ff7ab0" stroke="#b8326d" strokeWidth="0.7" transform="rotate(-35 67 23)" />
            <Strands c={hi} list={["M34 31 q10 -10 30 -8", "M38 28 q8 -7 24 -6"]} />
            <path d="M29 38 q-2 5 1 8" stroke={ink} strokeWidth="0.8" fill="none" />
          </g>
        ),
      };
    case "messybun":
      return {
        front: (
          <g>
            {P(cap(19, 46, 29))}
            <Curls fill={c} ink={ink} list={[[50, 13, 7], [44, 15, 4.5], [56, 15, 4.5], [50, 8, 4], [46, 9, 3.4], [54, 9, 3.4]]} />
            <path d="M30 40 q-3 6 0 12 M70 40 q3 6 0 12 M36 30 q-4 4 -4 10" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round" />
            <path d="M30 40 q-3 6 0 12 M70 40 q3 6 0 12" stroke={ink} strokeWidth="0.5" fill="none" />
            <Strands c={hi} list={["M36 30 q6 -6 13 -8", "M64 30 q-6 -6 -13 -8"]} />
          </g>
        ),
      };
    case "sidebraid":
      return {
        back: <path d={longBack(60)} fill={dk} stroke={ink} strokeWidth="1.1" />,
        front: (
          <g>
            {P(cap(17.5, 48, 29, 0.5))}
            {P("M38 21 C52 17 68 22 72 36 C66 30 56 29 48 31 C42 32 36 35 32 40 C32 31 34 25 38 21 Z")}
            <Braid x={70} y={44} len={40} w={3.6} fill={c} ink={ink} bend={4} />
            <rect x="71.6" y="84" width="5" height="2.4" rx="1.2" fill="#ff7ab0" stroke="#b8326d" strokeWidth="0.5" />
          </g>
        ),
      };
    case "bob":
      return {
        back: <path d="M24 38 C20 48 20 60 23 66 C34 70 66 70 77 66 C80 60 80 48 76 38 Z" fill={dk} stroke={ink} strokeWidth="1.2" />,
        front: (
          <g>
            {P(cap(17, 50, 30, 1))}
            {P("M40 21 C54 17 72 24 74 40 C75 50 76 58 78 64 C72 62 72 54 70 44 C67 36 58 31 46 33 C40 34 34 37 30 42 C30 32 34 24 40 21 Z")}
            {P("M30 42 C28 50 28 58 25 64 C22 58 22 48 24 40 Z")}
            <Strands c={hi} list={["M46 24 q14 0 22 14", "M27 46 l-1 12"]} />
          </g>
        ),
      };
    case "scarf": {
      const f = scarfColor || "#2f9e9a";
      const fi = shade(f, 0.6);
      return {
        back: <path d="M20 46 C18 24 32 13 50 13 C68 13 82 24 80 46 C82 62 80 74 84 86 L16 86 C20 74 18 62 20 46 Z" fill={f} stroke={fi} strokeWidth="1.3" />,
        front: (
          <g>
            <path d="M24 50 C22 28 34 17 50 17 C66 17 78 28 76 50 C74 34 64 26 50 26 C36 26 26 34 24 50 Z" fill={f} stroke={fi} strokeWidth="1.2" />
            <path d="M26 54 C27 66 38 72 50 72 C62 72 73 66 74 54 C76 62 74 74 66 80 C56 84 44 84 34 80 C26 74 24 62 26 54 Z" fill={f} stroke={fi} strokeWidth="1.2" />
            <path d="M32 24 q18 -8 36 0" stroke={shade(f, 1.3)} strokeWidth="1" fill="none" />
          </g>
        ),
      };
    }
    default:
      return hairArt("short", c, gid, scarfColor);
  }
};

// ---------------------------------------------------------------------------
// FACE
// ---------------------------------------------------------------------------
const Eye = ({ x, y, color, kind, left }) => {
  if (kind === 2 || (kind === 4 && !left)) {
    // happy / wink: closed smiling arcs
    return <path d={`M${x - 4.4} ${y + 0.8} Q${x} ${y - 4.2} ${x + 4.4} ${y + 0.8}`} stroke={INK} strokeWidth="1.7" fill="none" strokeLinecap="round" />;
  }
  const big = kind === 3 ? 1.08 : 1;
  return (
    <g>
      <ellipse cx={x} cy={y} rx={4.7 * big} ry={5.6 * big} fill="#fff" stroke={INK} strokeWidth="0.9" />
      <ellipse cx={x} cy={y + 0.6} rx={3.9 * big} ry={4.7 * big} fill={color} />
      <ellipse cx={x} cy={y + 0.9} rx={2.3 * big} ry={2.9 * big} fill="#140d0b" />
      <circle cx={x - 1.4} cy={y - 1.3} r={1.6 * big} fill="#fff" />
      <circle cx={x + 1.5} cy={y + 2.1} r={0.75 * big} fill="#fff" />
      {kind === 3 && <path d={`M${x + 2.2} ${y - 3.6} l0.5 1 1 0.5 -1 0.5 -0.5 1 -0.5 -1 -1 -0.5 1 -0.5 z`} fill="#fff" />}
      <path d={`M${x - 4.9} ${y - 1.4} Q${x} ${y - 7.6} ${x + 4.9} ${y - 1.4}`} stroke={INK} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {kind === 1 && (
        <path
          d={left ? `M${x - 4.4} ${y - 2.8} l-2 -1.4 M${x - 3.2} ${y - 4.2} l-1.4 -1.8` : `M${x + 4.4} ${y - 2.8} l2 -1.4 M${x + 3.2} ${y - 4.2} l1.4 -1.8`}
          stroke={INK}
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      )}
    </g>
  );
};

const Mouth = ({ kind }) => {
  const lip = "#7a2f25";
  switch (kind) {
    case 1:
      return (
        <g>
          <path d="M44.5 56.5 Q50 63.5 55.5 56.5 Z" fill="#8c2f2a" stroke={lip} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M45.6 57.1 L54.4 57.1 L53.8 58.6 L46.2 58.6 Z" fill="#fff" />
          <path d="M47.5 61 Q50 59.6 52.5 61" fill="#ef7f7a" />
        </g>
      );
    case 2:
      return (
        <g>
          <path d="M43.5 56 Q50 66 56.5 56 Z" fill="#8c2f2a" stroke={lip} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M44.7 56.6 L55.3 56.6 L54.6 58.4 L45.4 58.4 Z" fill="#fff" />
          <path d="M46.5 62.3 Q50 59.5 53.5 62.3" fill="#ef7f7a" />
        </g>
      );
    case 3:
      return <path d="M47 57.5 Q50 60 53 57.5" stroke={lip} strokeWidth="1.4" fill="none" strokeLinecap="round" />;
    case 4:
      return <ellipse cx="50" cy="58.6" rx="2.4" ry="2.9" fill="#8c2f2a" stroke={lip} strokeWidth="1" />;
    default:
      return <path d="M45 56.8 Q50 62 55 56.8" stroke={lip} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
  }
};

const colourOf = (id, fallback) => CATALOG_BY_ID[id]?.fill || fallback;

// ---------------------------------------------------------------------------
// PAID FACE EXTRAS (avatarCatalog.json kinds "eyes", "mouth", "mustache")
// ---------------------------------------------------------------------------
const heartPath = (x, y, r) => `M${x} ${y + r * 0.9} C${x - r * 1.5} ${y - r * 0.1} ${x - r * 0.9} ${y - r * 1.3} ${x} ${y - r * 0.45} C${x + r * 0.9} ${y - r * 1.3} ${x + r * 1.5} ${y - r * 0.1} ${x} ${y + r * 0.9} Z`;
const starD = (cx, cy, r) => {
  let d = "";
  for (let i = 0; i < 10; i += 1) {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 ? r * 0.45 : r;
    d += `${i ? "L" : "M"}${(cx + Math.cos(a) * rr).toFixed(2)} ${(cy + Math.sin(a) * rr).toFixed(2)} `;
  }
  return `${d}Z`;
};

const SpecialEyes = ({ style, color, rid }) => {
  const xs = [40.5, 59.5];
  const y = 46.5;
  if (style === "hearts")
    return (
      <g>
        {xs.map((x) => (
          <g key={x}>
            <path d={heartPath(x, y, 4.6)} fill="#ff4d7d" stroke={INK} strokeWidth="0.9" />
            <ellipse cx={x - 1.6} cy={y - 1.6} rx="1.1" ry="0.8" fill="#fff" opacity="0.85" />
          </g>
        ))}
      </g>
    );
  if (style === "stars")
    return (
      <g>
        {xs.map((x) => (
          <g key={x}>
            <path d={starD(x, y, 5.6)} fill="#ffd23f" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />
            <circle cx={x - 1.2} cy={y - 1.4} r="1" fill="#fff" />
          </g>
        ))}
      </g>
    );
  if (style === "cool")
    return (
      <g>
        {xs.map((x) => (
          <g key={x}>
            <ellipse cx={x} cy={y + 0.8} rx="4.7" ry="4.2" fill="#fff" stroke={INK} strokeWidth="0.9" />
            <ellipse cx={x + 0.8} cy={y + 1.6} rx="2.8" ry="2.8" fill={color} />
            <circle cx={x + 0.8} cy={y + 1.8} r="1.5" fill="#140d0b" />
            <path d={`M${x - 5.2} ${y - 0.4} L${x + 5.2} ${y - 0.4} L${x + 5} ${y - 4} Q${x} ${y - 6} ${x - 5} ${y - 4} Z`} fill={INK} />
          </g>
        ))}
      </g>
    );
  // rainbow sparkle
  return (
    <g>
      <defs>
        <linearGradient id={`${rid}rbeye`} x1="0" y1="0" x2="0" y2="1">
          {["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff", "#8b5cf6"].map((c, i) => (
            <stop key={c} offset={i / 4} stopColor={c} />
          ))}
        </linearGradient>
      </defs>
      {xs.map((x) => (
        <g key={x}>
          <ellipse cx={x} cy={y} rx="4.9" ry="5.8" fill="#fff" stroke={INK} strokeWidth="0.9" />
          <ellipse cx={x} cy={y + 0.6} rx="4.1" ry="4.9" fill={`url(#${rid}rbeye)`} />
          <ellipse cx={x} cy={y + 0.9} rx="2" ry="2.6" fill="#140d0b" />
          <path d={starD(x - 1.4, y - 1.6, 1.8)} fill="#fff" />
          <circle cx={x + 1.6} cy={y + 2.1} r="0.8" fill="#fff" />
          <path d={`M${x - 4.9} ${y - 1.4} Q${x} ${y - 7.8} ${x + 4.9} ${y - 1.4}`} stroke={INK} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </g>
      ))}
      <path d={starD(68, 38, 1.6)} fill="#ffd23f" />
      <path d={starD(32, 39, 1.2)} fill="#7fd3ff" />
    </g>
  );
};

const SpecialMouth = ({ style }) => {
  const lip = "#7a2f25";
  switch (style) {
    case "smirk":
      return <path d="M45 57.8 Q50 59.6 55.6 55.6" stroke={lip} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
    case "tongue":
      return (
        <g>
          <path d="M44.6 56.6 Q50 61.6 55.4 56.6" stroke={lip} strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <path d="M47.6 58.4 Q47.6 63.4 50.4 63.4 Q53.2 63.4 53 58.6 Z" fill="#ff7a92" stroke={lip} strokeWidth="1" />
          <path d="M50.3 59.2 L50.3 62" stroke="#e0566f" strokeWidth="0.7" />
        </g>
      );
    case "braces":
      return (
        <g>
          <path d="M44.5 56.5 Q50 63.5 55.5 56.5 Z" fill="#8c2f2a" stroke={lip} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M45.4 57 L54.6 57 L54 59 L46 59 Z" fill="#fff" />
          <path d="M45.6 58 L54.4 58" stroke="#8b98aa" strokeWidth="0.8" />
          {[46.8, 48.9, 51.1, 53.2].map((x) => (
            <rect key={x} x={x - 0.55} y="57.4" width="1.1" height="1.1" fill="#b8c2d0" stroke="#6b7685" strokeWidth="0.25" />
          ))}
        </g>
      );
    default:
      // goofy grin: big open smile with two buck teeth
      return (
        <g>
          <path d="M43.6 56 Q50 66 56.4 56 Z" fill="#8c2f2a" stroke={lip} strokeWidth="1.1" strokeLinejoin="round" />
          <rect x="47.9" y="56.2" width="2" height="2.6" rx="0.4" fill="#fff" stroke="#c9ccd3" strokeWidth="0.3" />
          <rect x="50.1" y="56.2" width="2" height="2.6" rx="0.4" fill="#fff" stroke="#c9ccd3" strokeWidth="0.3" />
          <path d="M46.6 62.2 Q50 59.6 53.4 62.2" fill="#ef7f7a" />
        </g>
      );
  }
};

/** Mustache under the nose. Natural ones use the hair colour. */
const Mustache = ({ style, color, rid }) => {
  const fill = style === "rainbow" ? `url(#${rid}rbmust)` : color;
  const ink = style === "rainbow" ? "#5b3a8a" : shade(color, 0.55);
  const P = { fill, stroke: ink, strokeWidth: 0.8, strokeLinejoin: "round" };
  let d;
  switch (style) {
    case "handlebar":
      d = "M50 54.4 C47 53.6 44 54.2 42.6 55.6 C41.6 56.6 40.4 56 40.2 54.6 C40 56.8 42 58 44 57.2 C46.4 56.4 48.4 55.8 50 56.2 C51.6 55.8 53.6 56.4 56 57.2 C58 58 60 56.8 59.8 54.6 C59.6 56 58.4 56.6 57.4 55.6 C56 54.2 53 53.6 50 54.4 Z";
      break;
    case "curly":
      d = "M50 54.4 C47 53.4 43.4 54 42.4 56 C41.6 57.6 39.6 57.4 39.6 55.6 C39.6 54.4 41 54 41.2 55.2 C40.6 59 45.6 58 50 56.4 C54.4 58 59.4 59 58.8 55.2 C59 54 60.4 54.4 60.4 55.6 C60.4 57.4 58.4 57.6 57.6 56 C56.6 54 53 53.4 50 54.4 Z";
      break;
    case "chevron":
      d = "M50 54 C46 53.6 43 54.6 42.6 57.6 C45 57.6 47.6 57 50 56.6 C52.4 57 55 57.6 57.4 57.6 C57 54.6 54 53.6 50 54 Z";
      break;
    default:
      // classic (and rainbow): a friendly rounded walrus-lite shape
      d = "M50 54.2 C46.4 53.4 43.2 54.4 43 56.6 C44.6 57.6 47.4 57.4 50 56.4 C52.6 57.4 55.4 57.6 57 56.6 C56.8 54.4 53.6 53.4 50 54.2 Z";
  }
  return (
    <g>
      {style === "rainbow" && (
        <defs>
          <linearGradient id={`${rid}rbmust`} x1="0" y1="0" x2="1" y2="0">
            {["#ff4d6d", "#ff9a3c", "#ffd23f", "#4cd964", "#3a86ff", "#8b5cf6"].map((c, i) => (
              <stop key={c} offset={i / 5} stopColor={c} />
            ))}
          </linearGradient>
        </defs>
      )}
      <path d={d} {...P} />
    </g>
  );
};

// ---------------------------------------------------------------------------
// PICTURE ITEMS (Nov 2026) — jackets & shirts, shoes, mustaches & beards cut
// from the art sheets. They're pictures in public/avatar-wear/…, listed in
// avatarCatalog.json with `img` plus where they sit:
//   tops      ar (width ÷ height), sh (how far down the picture the shoulders
//             are, 0–1) — they replace the drawn shirt AND its sleeves;
//             arms "short" = short sleeves (bare arms in full body)
//   shoes     ar — drawn on each foot (the right one mirrored); pair = the
//             picture already shows both shoes, drawn once across the feet
//   mustache  ar, fit [top, width] on the face, layer "over" (after the
//             mouth: mustaches) or "under" (before it, so the mouth shows
//             through a beard); tinted to the hair colour unless keepColor
// ---------------------------------------------------------------------------
// (the build swaps process.env.PUBLIC_URL for the app's base path; "" at the root)
let PUBLIC_URL = "";
try {
  PUBLIC_URL = process.env.PUBLIC_URL || "";
} catch {
  /* no build-time value */
}
export const wearImg = (p) => (!p || /^(https?:|data:|blob:)/.test(p) ? p : `${PUBLIC_URL}${p}`);
export const isPictureItem = (id) => Boolean(id && CATALOG_BY_ID[id]?.img);

/** where a picture top sits: portrait (shoulders at y 79) or full body */
const pictureTopBox = (it, full) => {
  const ar = Number(it.ar) || 1.1;
  const sh = Number.isFinite(Number(it.sh)) ? Number(it.sh) : 0.16;
  const w = full ? 58 : 86;
  const h = w / ar;
  const shoulderY = full ? 76 : 77.5;
  return { x: 50 - w / 2, y: shoulderY - sh * h, w, h };
};

const PictureTop = ({ it, full, skin, skinInk }) => {
  const b = pictureTopBox(it, full);
  const short = it.arms === "short";
  return (
    <g>
      {/* short sleeves: bare arms down to the hands, behind the sleeves */}
      {full &&
        short &&
        [false, true].map((right) => {
          const hx = right ? b.x + b.w * 0.885 : b.x + b.w * 0.115;
          const top = b.y + b.h * 0.45;
          const bottom = b.y + b.h + 1.6;
          return <path key={right ? "ar" : "al"} d={`M${hx - 2.6} ${top} L${hx + 2.6} ${top} L${hx + 2.4} ${bottom} L${hx - 2.4} ${bottom} Z`} fill={skin} stroke={skinInk} strokeWidth="0.9" />;
        })}
      <image href={wearImg(it.img)} x={b.x} y={b.y} width={b.w} height={b.h} preserveAspectRatio="none" />
      {full &&
        [false, true].map((right) => {
          // hands come out of the cuffs at the bottom corners of the picture
          const hx = right ? b.x + b.w * 0.885 : b.x + b.w * 0.115;
          return <circle key={right ? "r" : "l"} cx={hx} cy={b.y + b.h + 1.6} r="3.7" fill={skin} stroke={skinInk} strokeWidth="1" />;
        })}
    </g>
  );
};

/** where a picture shoe sits on the left foot (the right one is mirrored) */
const pictureShoeBox = (it) => {
  const ar = Number(it.ar) || 1.5;
  const w = 27;
  const h = w / ar;
  return { x: 24, y: 158.4 - h, w, h };
};
/**
 * The pants hem for a picture shoe: the trousers go down INTO the shoe, so
 * the hole the foot goes in is covered and the top of the shoe sits flush
 * with the pants. `hem` (0–1, how far down the picture) can be set per shoe.
 */
export const pictureShoeHem = (it) => {
  const b = pictureShoeBox(it);
  const f = Number.isFinite(Number(it.hem)) ? Number(it.hem) : 0.42;
  return b.y + b.h * f;
};

/** a picture shoe on each foot (the sheet's shoes point left = the left foot) */
const PictureShoes = ({ it }) => {
  const ar = Number(it.ar) || 1.5;
  if (it.pair) {
    // a picture of BOTH shoes (seen from the front): one picture across the feet
    const pw = 40;
    const ph = pw / ar;
    return <image href={wearImg(it.img)} x={50 - pw / 2} y={158.6 - ph} width={pw} height={ph} preserveAspectRatio="none" />;
  }
  const { x, y, w, h } = pictureShoeBox(it);
  return (
    <g>
      <image href={wearImg(it.img)} x={x} y={y} width={w} height={h} preserveAspectRatio="none" />
      <g transform="translate(100 0) scale(-1 1)">
        <image href={wearImg(it.img)} x={x} y={y} width={w} height={h} preserveAspectRatio="none" />
      </g>
    </g>
  );
};

/** a picture mustache / beard, in the hair colour (white beards keep theirs) */
const PictureBeard = ({ it, hair, rid, skin }) => {
  const [top, width] = Array.isArray(it.fit) ? it.fit : [53.4, 20];
  const ar = Number(it.ar) || 2;
  const h = width / ar;
  const tint = !it.keepColor && hair;
  let m = null;
  if (tint) {
    // keep the drawing's light and shade, in the hair colour: brightness of
    // each pixel (the sheet's brown is ~0.33) times the hair colour
    const n = parseInt(String(hair).slice(1), 16);
    const k = [n >> 16, (n >> 8) & 255, n & 255].map((v) => v / 255 / 0.36);
    m = k.map((c) => `${(0.3 * c).toFixed(3)} ${(0.59 * c).toFixed(3)} ${(0.11 * c).toFixed(3)} 0 0`).join(" ") + " 0 0 0 1 0";
  }
  // a beard that hangs below the chin: anything showing through its mouth
  // gap is skin, not the shirt behind the face
  const chin = it.layer !== "over" && h > 14 ? <ellipse cx="50" cy={top + h * 0.5} rx={width * 0.3} ry={h * 0.36} fill={skin} /> : null;
  return (
    <g>
      {chin}
      {tint && (
        <defs>
          <filter id={`${rid}beard`} colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values={m} />
          </filter>
        </defs>
      )}
      <image
        href={wearImg(it.img)}
        x={50 - width / 2}
        y={top}
        width={width}
        height={h}
        preserveAspectRatio="none"
        filter={tint ? `url(#${rid}beard)` : undefined}
      />
    </g>
  );
};

// ---------------------------------------------------------------------------
// FULL BODY — the same head and chest, then the shirt carries on down to the
// waist, with arms, trousers and shoes. Drawn in a 100 x 170 box: the top
// 100 x 100 is exactly the portrait (so hats and wings line up), the chest is
// drawn smaller so the body is in proportion.
// ---------------------------------------------------------------------------
export const FULL_H = 170;
const NECK_Y = 71.5; // where the collar sits (Top is drawn 3 higher than its own 74.5)
export const TORSO_SCALE = 0.62;
// the chest is drawn a little narrower than tall, so the body reads slimmer
export const TORSO_SCALE_X = 0.56;
const SHORT_SLEEVES = new Set(["tee", "polo", "jersey"]);
const DEFAULT_LEGS = { pants: "#3f5f9e", shoes: "#ffffff", sole: "#c9d1de" };
const mirror = (d) => d.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (m, x, y) => `${(100 - Number(x)).toFixed(2)} ${y}`);
const ARM_LONG = "M27.6 79.4 C22.6 81.4 19.6 88 18.8 98 L18.2 107.5 Q22.2 110 26.2 107.5 L27.2 99 C27.8 92 29 88.4 31.6 85.4 Z";
const ARM_SHORT = "M27.6 79.4 C22.6 81.4 19.6 88 19.2 94.5 L27.4 96.2 C28 91.5 29 88.4 31.6 85.4 Z";
const ARM_SKIN = "M19.6 92 L18.6 107.5 Q22.4 110 26.2 107.5 L27.2 94 Z";
const HAND_X = 22.4;
const LEG = "M32.4 111 L49.2 111 L48.4 149.5 L34.2 149.5 Z";
const THIGH = "M32.4 111 L49.2 111 L48.9 127.5 L33.1 127.5 Z"; // shorts
const SHIN = "M33.4 127 L48.6 127 L48.4 149.5 L34.4 149.5 Z";
const SHOE = "M32 149 L48.8 149 L48.8 152.4 Q48.8 156.6 44.6 156.6 L31.4 156.6 Q26.8 156.6 26.8 153 Q26.8 149.6 32 149 Z";
const ANKLE = "M33.6 140.5 L48.6 140.5 L48.8 150.5 L33.2 150.5 Z"; // high-tops, boots
const BOOT = "M33.4 133.5 L48.7 133.5 L48.8 150.5 L33 150.5 Z";
const SKIRT_LEG = "M34.6 128 L47.6 128 L47.4 149.5 L35.2 149.5 Z";
const SKIRT = "M29.6 110 L70.4 110 L76 131 Q50 136 24 131 Z";
// the shirt carries on down to a slimmer waist
const WAIST = "M26.4 88 L73.6 88 C72.6 96 71 104 70.6 113 Q50 116.4 29.4 113 C29 104 27.4 96 26.4 88 Z";

/** paint for a pants / shoes item: plain colour or a pattern */
const wearPaint = (it, rid, key) => {
  if (!it) return { fill: null, defs: null };
  const id = `${rid}${key}`;
  const c = it.fill || "#3f5f9e";
  const c2 = it.second || "#ffffff";
  switch (it.style) {
    case "plaid":
      return {
        fill: `url(#${id})`,
        defs: (
          <pattern id={id} width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill={c} />
            <rect width="8" height="2.4" y="2.8" fill={c2} opacity="0.55" />
            <rect width="2.4" height="8" x="2.8" fill={c2} opacity="0.55" />
            <rect width="8" height="0.6" y="6.6" fill="#fff" opacity="0.35" />
          </pattern>
        ),
      };
    case "camo":
      return {
        fill: `url(#${id})`,
        defs: (
          <pattern id={id} width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill={c} />
            <path d="M1 3 Q4 0 7 3 Q6 6 2 6 Z M8 9 Q12 7 13 11 Q10 13 8 12 Z" fill={shade(c, 0.6)} />
            <path d="M9 1 Q12 2 11 5 Q8 5 9 1 Z M2 9 Q5 8 5 12 Q2 13 2 9 Z" fill={c2} />
          </pattern>
        ),
      };
    case "sparkle":
      return {
        fill: `url(#${id})`,
        defs: (
          <pattern id={id} width="7" height="7" patternUnits="userSpaceOnUse">
            <rect width="7" height="7" fill={c} />
            <circle cx="1.6" cy="1.8" r="0.7" fill="#fff" opacity="0.9" />
            <circle cx="5" cy="4.6" r="0.55" fill="#fff6b0" />
            <circle cx="2.6" cy="5.8" r="0.4" fill="#fff" opacity="0.7" />
          </pattern>
        ),
      };
    case "rainbow":
      return {
        fill: `url(#${id})`,
        defs: (
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            {["#ff4d6d", "#ff9a3c", "#ffd23f", "#4cd964", "#3a86ff", "#8b5cf6"].map((col, i) => (
              <stop key={col} offset={`${(i / 5) * 100}%`} stopColor={col} />
            ))}
          </linearGradient>
        ),
      };
    case "galaxy":
      return {
        fill: `url(#${id})`,
        defs: (
          <pattern id={id} width="12" height="12" patternUnits="userSpaceOnUse">
            <rect width="12" height="12" fill={c} />
            <circle cx="6" cy="6" r="5" fill="#7b5cff" opacity="0.35" />
            <circle cx="2" cy="3" r="0.6" fill="#fff" />
            <circle cx="9" cy="9.5" r="0.5" fill="#fff" />
            <circle cx="9.5" cy="2.5" r="0.35" fill="#ffe680" />
          </pattern>
        ),
      };
    default:
      return { fill: c, defs: null };
  }
};

const FullBodyBelow = ({ look, paint, fill, skin, skinInk, legs: L0, rid, pictureTop = false }) => {
  const legs = { ...DEFAULT_LEGS, ...(L0 || {}) };
  // the student's own pants / shoes (maker), unless a costume brings its own
  const pantsIt = look.pants && !legs.ownPants && !legs.skirt && !legs.stripes ? CATALOG_BY_ID[look.pants] : null;
  const shoesIt = look.shoes && !legs.ownShoes ? CATALOG_BY_ID[look.shoes] : null;
  const pp = wearPaint(pantsIt, rid, "pants");
  const sp = wearPaint(shoesIt?.style === "sparkle" ? { ...shoesIt, style: "sparkle" } : null, rid, "shoes");
  const pStyle = pantsIt?.style || "plain";
  const pantsCol = pantsIt?.fill || legs.pants;
  const pantsFill = pp.fill || legs.pants;
  const sStyle = shoesIt?.style || "sneaker";
  const shoeCol = shoesIt?.fill || legs.shoes;
  const shoeFill = sp.fill || shoeCol;
  const whiteish = shoeCol === "#ffffff";

  const base = legs.body || paint?.fill || "#2f6fd6";
  const bodyFill = legs.body || fill;
  const ink = shade(base, 0.55);
  const pantsInk = shade(pStyle === "rainbow" ? "#8b5cf6" : pantsCol, 0.55);
  const kind = String(look.top || "").replace("av-top-", "");
  const short = legs.sleeveLong === undefined ? SHORT_SLEEVES.has(kind) : !legs.sleeveLong;
  const sleeveFill = legs.sleeve || (kind === "varsity" ? "#f4f1e8" : bodyFill);
  const sleeveInk = shade(legs.sleeve || (kind === "varsity" ? "#d8d2c0" : base), 0.6);
  // picture shoes: the legs are drawn over them, down to the shoe's collar
  const hem = shoesIt?.img && !shoesIt.pair ? pictureShoeHem(shoesIt) : null;
  const legShape = (right, key) => {
    const f = (d) => {
      const dd = hem ? d.replace(/149\.5/g, hem.toFixed(2)).replace(/ 145 /g, ` ${(hem - 4.5).toFixed(2)} `) : d;
      return right ? mirror(dd) : dd;
    };
    if (legs.skirt) {
      return <path key={key} d={f(SKIRT_LEG)} fill={legs.stripes ? `url(#${rid}tights)` : skin} stroke={legs.stripes ? shade(legs.stripes, 0.6) : skinInk} strokeWidth="1" />;
    }
    if (pStyle === "shorts") {
      return (
        <g key={key}>
          <path d={f(SHIN)} fill={skin} stroke={skinInk} strokeWidth="1" />
          <path d={f(THIGH)} fill={pantsFill} stroke={pantsInk} strokeWidth="1.1" strokeLinejoin="round" />
          {pantsIt?.second && <path d={f("M34.6 112 L34 126.6")} stroke={pantsIt.second} strokeWidth="1.6" />}
        </g>
      );
    }
    return (
      <g key={key}>
        <path d={f(LEG)} fill={legs.stripes ? `url(#${rid}tights)` : pantsFill} stroke={legs.stripes ? shade(legs.stripes, 0.6) : pantsInk} strokeWidth="1.1" strokeLinejoin="round" />
        {pStyle === "joggers" && <path d={f("M34 145 L48.5 145 L48.4 149.5 L34.2 149.5 Z")} fill={shade(pantsCol, 0.78)} stroke={pantsInk} strokeWidth="0.8" />}
        {pStyle === "joggers" && <path d={f("M36 112 L35.4 146")} stroke={pantsIt.second || "#ffffff"} strokeWidth="1.2" opacity="0.9" />}
        {pStyle === "cargo" && <path d={f("M32.6 124 L38.6 124 L38.4 133.5 L32.9 133.5 Z M33 126.4 L38.5 126.4")} fill={shade(pantsCol, 0.85)} stroke={pantsInk} strokeWidth="0.8" />}
        {(pStyle === "jeans" || pStyle === "ripped") && <path d={f("M33.4 113 L35.2 120 M47.4 113 L45.6 119")} stroke="#f2c230" strokeWidth="0.6" strokeDasharray="1 0.8" fill="none" opacity="0.8" />}
        {pStyle === "ripped" && <path d={f("M37 127 Q41 125.4 45 127 Q41 129.6 37 127 Z M38 136 L44 136")} fill={skin} stroke={pantsInk} strokeWidth="0.6" />}
        {pStyle === "leggings" && <path d={f("M34 113 L35.6 148")} stroke={shade(pantsCol, 1.25)} strokeWidth="1" opacity="0.6" />}
      </g>
    );
  };

  const shoe = (right) => {
    const f = (d) => (right ? mirror(d) : d);
    const sInk = shade(whiteish ? "#9aa3b5" : shoeCol, 0.6);
    if (sStyle === "sandal") {
      return (
        <g key={right ? "r" : "l"}>
          <path d={f(SHOE)} fill={skin} stroke={skinInk} strokeWidth="1" />
          <path d={f("M26.8 154.8 L48.8 154.8")} stroke={shade(shoeCol, 0.7)} strokeWidth="2.4" />
          <path d={f("M31 150 L46 155 M46 150 L33 155")} stroke={shoeCol} strokeWidth="2" strokeLinecap="round" />
        </g>
      );
    }
    const tall = sStyle === "boot" || sStyle === "rainboot" ? BOOT : sStyle === "hightop" ? ANKLE : null;
    return (
      <g key={right ? "r" : "l"}>
        {tall && <path d={f(tall)} fill={shoeFill} stroke={sInk} strokeWidth="1.1" strokeLinejoin="round" />}
        <path d={f(SHOE)} fill={shoeFill} stroke={sInk} strokeWidth="1.1" strokeLinejoin="round" />
        {sStyle === "lightup" && [29.5, 34, 38.5, 43].map((x, i) => <circle key={x} cx={right ? 100 - x : x} cy="154.4" r="1.1" fill={["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff"][i]} />)}
        {sStyle === "rainboot" && <path d={f("M35 136 L35 148")} stroke="#fff" strokeWidth="1.4" opacity="0.6" strokeLinecap="round" />}
        {sStyle === "boot" && <path d={f("M38 137 L44 137 M38 140.5 L44 140.5 M38 144 L44 144")} stroke={shade(shoeCol, 1.35)} strokeWidth="0.8" />}
        {sStyle === "hightop" && <circle cx={right ? 59.5 : 40.5} cy="145" r="2.6" fill="#fff" stroke={sInk} strokeWidth="0.7" />}
        {(sStyle === "sneaker" || sStyle === "hightop" || sStyle === "lightup" || sStyle === "sparkle") && <path d={f("M48.8 154.4 L26.8 154.4")} stroke={sStyle === "lightup" ? `url(#${rid}glow)` : whiteish || !shoesIt ? legs.sole : shoesIt.second || "#ffffff"} strokeWidth={sStyle === "lightup" ? 2.8 : 2.2} />}
        {(sStyle === "sneaker" || sStyle === "lightup" || sStyle === "sparkle") && <path d={f("M42 150.4 L38 153")} stroke={whiteish ? paint?.fill || "#3a86ff" : shoesIt?.second || "#ffffff"} strokeWidth="1.6" strokeLinecap="round" />}
      </g>
    );
  };

  return (
    <g>
      <defs>
        {legs.stripes && (
          <pattern id={`${rid}tights`} width="6" height="6" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill={legs.pants} />
            <rect width="6" height="3" fill={legs.stripes} />
          </pattern>
        )}
        {pp.defs}
        {sp.defs}
        {sStyle === "lightup" && (
          <linearGradient id={`${rid}glow`} gradientUnits="userSpaceOnUse" x1="26" y1="0" x2="74" y2="0">
            {["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff", "#c06cff"].map((col, i) => (
              <stop key={col} offset={`${i * 25}%`} stopColor={col} />
            ))}
          </linearGradient>
        )}
      </defs>
      {/* legs + shoes (picture shoes: shoes first, then the legs into them) */}
      {!hem && legShape(false, "l")}
      {!hem && legShape(true, "r")}
      {!legs.skirt && hem === null && !legs.stripes && (pStyle === "plain" || pStyle === "jeans") && <path d="M41.2 114 L41.4 147 M58.8 114 L58.6 147" stroke={shade(pantsCol, 0.8)} strokeWidth="0.8" fill="none" opacity="0.7" />}
      <ellipse cx="50" cy="157.6" rx="26" ry="3.2" fill="#000" opacity="0.12" />
      {sStyle === "lightup" && <ellipse cx="50" cy="157.4" rx="24" ry="2.6" fill="#ffe66b" opacity="0.45" />}
      {shoesIt?.img ? (
        <>
          <PictureShoes it={shoesIt} />
          {hem && legShape(false, "l")}
          {hem && legShape(true, "r")}
          {hem && !legs.skirt && !legs.stripes && (pStyle === "plain" || pStyle === "jeans") && (
            <path d={`M41.2 114 L41.4 ${(hem - 2).toFixed(2)} M58.8 114 L58.6 ${(hem - 2).toFixed(2)}`} stroke={shade(pantsCol, 0.8)} strokeWidth="0.8" fill="none" opacity="0.7" />
          )}
        </>
      ) : (
        <>
          {shoe(false)}
          {shoe(true)}
        </>
      )}
      {/* skirt / dress */}
      {legs.skirt && <path d={SKIRT} fill={bodyFill} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />}
      {legs.skirt && legs.trim && <path d="M24.4 129.6 Q50 134.6 75.6 129.6" stroke={legs.trim} strokeWidth="3" fill="none" />}
      {/* the shirt carries on down to the waist (a picture top brings its own) */}
      {!pictureTop && <path d={WAIST} fill={bodyFill} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />}
      {legs.trim && !legs.skirt && <path d="M29.6 111.4 Q50 115 70.4 111.4" stroke={legs.trim} strokeWidth="3.2" fill="none" />}
      {legs.belt && <rect x="29" y="104.5" width="42" height="4.4" rx="1" fill={legs.belt} stroke={shade(legs.belt, 0.6)} strokeWidth="0.6" />}
      {legs.belt && <rect x="46.6" y="103.7" width="6.8" height="6" rx="1" fill="none" stroke="#f2c230" strokeWidth="1.3" />}
      {/* overalls: the bib and straps go over the shirt */}
      {pStyle === "overalls" && (
        <g>
          <path d="M37.5 97 L62.5 97 L63.4 114 Q50 116.4 36.6 114 Z" fill={pantsFill} stroke={pantsInk} strokeWidth="1" strokeLinejoin="round" />
          <path d="M42 100.5 L58 100.5 L57.6 107 L42.4 107 Z" fill={shade(pantsCol, 0.85)} stroke={pantsInk} strokeWidth="0.6" />
          <path d="M38.6 97.4 L34 86.6 M61.4 97.4 L66 86.6" stroke={pantsCol} strokeWidth="2.6" strokeLinecap="round" />
          <circle cx="39.4" cy="98.6" r="1.1" fill="#f2c230" />
          <circle cx="60.6" cy="98.6" r="1.1" fill="#f2c230" />
        </g>
      )}
      {/* arms (sleeves, then hands) — a picture top has its own sleeves */}
      {!pictureTop && [false, true].map((right) => {
        const f = (d) => (right ? mirror(d) : d);
        return (
          <g key={right ? "r" : "l"}>
            {short && <path d={f(ARM_SKIN)} fill={skin} stroke={skinInk} strokeWidth="1" />}
            <path d={f(short ? ARM_SHORT : ARM_LONG)} fill={sleeveFill} stroke={sleeveInk} strokeWidth="1.1" strokeLinejoin="round" />
            {!short && legs.trim && <path d={f("M18.4 104.6 Q22.4 107 26.5 104.6")} stroke={legs.trim} strokeWidth="3" fill="none" strokeLinecap="round" />}
            <circle cx={right ? 100 - HAND_X : HAND_X} cy="110.6" r="4.1" fill={skin} stroke={skinInk} strokeWidth="1" />
          </g>
        );
      })}
    </g>
  );
};

/**
 * The avatar. `full` draws the whole body (100 x 170 box) instead of the
 * head-and-chest portrait. `legs` (full only) colours the trousers / shoes —
 * seasonal outfits pass their own.
 */
export const AvatarFace = ({ look: raw, full = false, legs: legsIn = null, noBg = false }) => {
  useAvatarItemsVersion(); // uploaded items (avatarItems) arrive → draw them
  const rid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const look = cleanLook(raw);
  // Seasons items carry a drawing style instead of using the everyday parts
  const sTop = CATALOG_BY_ID[look.top]?.season ? CATALOG_BY_ID[look.top].style : "";
  const sGlasses = look.glasses && CATALOG_BY_ID[look.glasses]?.season ? CATALOG_BY_ID[look.glasses].style : "";
  const sPin = look.pin && CATALOG_BY_ID[look.pin]?.season ? CATALOG_BY_ID[look.pin].style : "";
  const sBg = look.bgItem ? CATALOG_BY_ID[look.bgItem]?.style : "";
  const sDeco = look.hairDeco ? CATALOG_BY_ID[look.hairDeco]?.style : "";
  const legs = legsIn || (sTop ? outfitLegs(sTop) : null);
  const skin = skinColour(look);
  const skinInk = shade(skin, 0.62);
  const skinDk = shade(skin, 0.88);
  const hairC = colourOf(look.hairColor, "#3b2619");
  const topItem = CATALOG_BY_ID[look.topColor] || CATALOG_BY_ID["av-col-blue"];
  const scarf = look.hair === "scarf";
  const hair = hairArt(look.hair, hairC, `${rid}fade`, topItem.fill);
  const browC = scarf ? shade(skin, 0.45) : shade(hairC, hairC === "#efe0b6" || hairC === "#e2b866" ? 0.7 : 0.85);

  const picTop = !sTop && CATALOG_BY_ID[look.top]?.img ? CATALOG_BY_ID[look.top] : null;
  const beard = look.mustache && CATALOG_BY_ID[look.mustache]?.img ? CATALOG_BY_ID[look.mustache] : null;
  const H = full ? FULL_H : 100;
  const topFill = topItem?.pattern ? `url(#${rid}paint)` : topItem?.fill || "#2f6fd6";
  return (
    <svg viewBox={`0 0 100 ${H}`} className={`sbl-av-svg ${full ? "is-full" : ""}`} aria-hidden="true">
      <defs>{paintDefs(rid, topItem)}</defs>
      {noBg ? null : sBg ? (
        full ? <SeasonBgTall style={sBg} rid={`${rid}bg`} /> : <SeasonBg style={sBg} rid={`${rid}bg`} />
      ) : (
        <>
          <rect width="100" height={H} fill={BACKGROUNDS[look.bg]} />
          {full && <rect y="128" width="100" height="42" fill={shade(BACKGROUNDS[look.bg], 0.93)} />}
          <circle cx="16" cy="16" r="11" fill="#fff" opacity="0.35" />
          <circle cx="88" cy="70" r="7" fill="#fff" opacity="0.25" />
        </>
      )}

      {hair.back}

      {full && <FullBodyBelow look={look} paint={topItem} fill={topFill} skin={skin} skinInk={skinInk} legs={legs} rid={rid} pictureTop={Boolean(picTop)} />}

      {/* neck */}
      <path d="M44.5 58 L44.5 72 Q50 75 55.5 72 L55.5 58 Z" fill={skinDk} stroke={skinInk} strokeWidth="1" />
      <path d="M44.5 64 Q50 68.5 55.5 64 L55.5 60 Q50 64.5 44.5 60 Z" fill={shade(skin, 0.8)} opacity="0.6" />

      {/* clothes (drawn a little higher, so the neck is short and cute);
          full body: the same chest, smaller, so the body is in proportion */}
      {picTop && <PictureTop it={picTop} full={full} skin={skin} skinInk={skinInk} />}
      {/* a picture top: the neck goes over its collar, hiding the inside back */}
      {picTop && <path d="M44.5 58 L44.5 71.6 Q50 74.4 55.5 71.6 L55.5 58 Z" fill={skinDk} stroke={skinInk} strokeWidth="1" />}
      <g transform={full ? `translate(50 ${NECK_Y}) scale(${TORSO_SCALE_X} ${TORSO_SCALE}) translate(-50 -74.5)` : "translate(0 -3)"}>
        {picTop ? null : sTop ? <SeasonTop style={sTop} rid={`${rid}st`} /> : <Top id={look.top} paint={topItem} rid={rid} />}
        {full && look.pin && (sPin ? <SeasonPin style={sPin} y={85} /> : <Pin id={look.pin} y={85} />)}
      </g>

      {/* ears */}
      {!scarf && (
        <g>
          <ellipse cx="27.3" cy="47" rx="4.6" ry="6" fill={skin} stroke={skinInk} strokeWidth="1" />
          <path d="M28.6 44 q-2.4 2.6 0 6" stroke={skinInk} strokeWidth="0.9" fill="none" opacity="0.7" />
          <ellipse cx="72.7" cy="47" rx="4.6" ry="6" fill={skin} stroke={skinInk} strokeWidth="1" />
          <path d="M71.4 44 q2.4 2.6 0 6" stroke={skinInk} strokeWidth="0.9" fill="none" opacity="0.7" />
        </g>
      )}

      {/* head */}
      <ellipse cx="50" cy="44" rx="22.5" ry="22" fill={skin} stroke={skinInk} strokeWidth="1.1" />
      <ellipse cx="50" cy="31" rx="15" ry="5" fill={shade(skin, 0.94)} opacity="0.5" />

      {/* cheeks, freckles */}
      {look.blush && (
        <g fill="#ff7f7f" opacity="0.38">
          <ellipse cx="35.2" cy="55" rx="3.8" ry="2.3" />
          <ellipse cx="64.8" cy="55" rx="3.8" ry="2.3" />
        </g>
      )}
      {look.freckles && (
        <g fill={shade(skin, 0.66)}>
          {[[36.5, 52.5], [39.5, 54.5], [34, 55], [63.5, 52.5], [60.5, 54.5], [66, 55], [47.5, 52], [52.5, 52]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="0.75" />
          ))}
        </g>
      )}

      {/* brows, eyes, nose, mouth */}
      <path d="M35 38.6 Q40 35.6 45 37.6 M55 37.6 Q60 35.6 65 38.6" stroke={browC} strokeWidth="1.9" fill="none" strokeLinecap="round" />
      {look.eyesItem ? (
        <SpecialEyes style={CATALOG_BY_ID[look.eyesItem]?.style} color={EYE_COLORS[look.eyeColor]} rid={rid} />
      ) : (
        <>
          <Eye x={40.5} y={46.5} color={EYE_COLORS[look.eyeColor]} kind={look.eyes} left />
          <Eye x={59.5} y={46.5} color={EYE_COLORS[look.eyeColor]} kind={look.eyes} left={false} />
        </>
      )}
      <ellipse cx="50" cy="52.6" rx="1.9" ry="1.4" fill={shade(skin, 0.8)} />
      <ellipse cx="49.4" cy="52.1" rx="0.6" ry="0.4" fill="#fff" opacity="0.6" />
      {beard && beard.layer !== "over" && <PictureBeard it={beard} hair={hairC} rid={rid} skin={skin} />}
      {look.mouthItem ? <SpecialMouth style={CATALOG_BY_ID[look.mouthItem]?.style} /> : <Mouth kind={look.mouth} />}
      {beard && beard.layer === "over" && <PictureBeard it={beard} hair={hairC} rid={rid} skin={skin} />}
      {look.mustache && !beard && <Mustache style={CATALOG_BY_ID[look.mustache]?.style} color={hairC} rid={rid} />}

      {hair.front}
      {sDeco && <HairDeco style={sDeco} hair={look.hair} />}

      {!full && look.pin && (sPin ? <SeasonPin style={sPin} y={85} /> : <Pin id={look.pin} y={85} />)}
      {look.glasses && (sGlasses ? <SeasonEyewear style={sGlasses} rid={`${rid}ew`} /> : <Eyewear id={look.glasses} rid={rid} />)}
    </svg>
  );
};

export default AvatarFace;
